import { useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Testimonial = {
  text: string;
  author: string;
};

const testimonials: Testimonial[] = [
  {
    text: "We would like to express our sincere gratitude to each member of your staff for their outstanding contribution. Their dedication and hard work were instrumental in achieving our goals and maintaining a high standard of service. Their positive attitude and willingness to go above and beyond did not go unnoticed and were truly valued by our organization. That's why we included your team as part of our Employee Recognition Program. These efforts have made a lasting impression, and we would be delighted to collaborate with you moving forward. We are confident that your staff's commitment to excellence will continue to foster success for both our organizations.",
    author: "Operations Manager",
  },
  {
    text: "I wanted to express my heartfelt gratitude regarding my experience with your agency. My interaction with your team has been nothing short of exceptional, and I wanted to acknowledge the outstanding service provided to Dubai Safari Park. The team is always available to assist with last minute requests and tries their best to accommodate them in a timely manner.",
    author: "Dubai Safari Park",
  },
  {
    text: "Expo has certainly taken us all on a journey, and I only hope that as we near the end, we can all look back and be proud of everything we achieved together. We faced many challenges, short notice requests and constant changes as we navigated not only a six-month operational event, but covid times as well. Your support and dedication has been amazing and I hope you will look back on your time with us with fondness.",
    author: "Expo 2020 Client",
  },
  {
    text: "IMMACULATE has helped our business by providing the right manpower and managing the SLAs at all times.",
    author: "Valued Client",
  },
  {
    text: "Great company! Helped us get the right people to take our operations to the next level.",
    author: "Business Partner",
  },
  {
    text: "Every service that IMMACULATE offers, we utilize. Because we are that confident in what they deliver. They are empowering our business on every level!",
    author: "Long-term Client",
  },
];

const TestimonialsCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="hidden md:block bg-light-gray py-12 sm:py-16 md:py-20 lg:py-20">
      <div className="container px-5 sm:px-6 lg:px-[15px]">
        <div className="grid lg:grid-cols-12 lg:gap-10 xl:gap-12 items-center">
          <div className="lg:col-span-5 text-center lg:text-left mb-8 lg:mb-0">
            <p className="section-label text-xs sm:text-sm">Testimonials</p>
            <h2 className="mt-3 sm:mt-4 text-3xl sm:text-4xl lg:text-[44px] font-bold font-display text-brand-navy leading-tight">
              Reliable and<br />Trustworthy
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-base lg:text-lg text-dark-gray leading-[1.7]">
              Trust is the foundation of any successful business relationship, and we're thrilled 
              to know that our clients trust us. Your confidence in our brand and services is 
              what drives us to continually improve and fulfill our promises.
            </p>
            <div className="mt-8 sm:mt-10 lg:mt-12 flex gap-3 sm:gap-4 justify-center lg:justify-start">
              <button
                onClick={scrollPrev}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-border-subtle flex items-center justify-center text-brand-navy hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all duration-300"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button
                onClick={scrollNext}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-border-subtle flex items-center justify-center text-brand-navy hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all duration-300"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {testimonials.map((testimonial, index) => (
                  <div className="flex-[0_0_100%] min-w-0 px-2 sm:px-0" key={index}>
                    <div className="bg-white p-6 sm:p-8 md:p-10 rounded-md shadow-[0_5px_25px_rgba(0,0,0,0.08)]">
                      <div className="flex items-start gap-x-3 sm:gap-x-5">
                        <div className="hidden sm:block">
                          <span className="font-display text-6xl sm:text-7xl text-quote-blue opacity-50 leading-none -mt-3 block">"</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm sm:text-base lg:text-lg text-dark-gray leading-[1.7]">
                            {testimonial.text}
                          </p>
                          <div className="mt-6 sm:mt-8 flex items-center gap-3 sm:gap-4">
                            <div className="w-10 sm:w-12 h-px bg-brand-gold"></div>
                            <p className="quote-attribution text-sm">
                              {testimonial.author}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;